const DESKTOP_BREAKPOINT = '(min-width: 1024px)';

const safePlay = async (video) => {
  if (!video) return;

  try {
    const playPromise = video.play();

    if (playPromise && typeof playPromise.then === 'function') {
      await playPromise;
    }
  } catch (error) {
    // autoplay może zostać zablokowany przez przeglądarkę
  }
};

const activateVideoSources = (video) => {
  if (!video || video.dataset.videoLoaded === 'true') return;

  const videoSrc = video.getAttribute('data-src');

  if (videoSrc) {
    video.src = videoSrc;
  }

  const sources = video.querySelectorAll('source[data-src]');

  if (sources.length) {
    sources.forEach((source) => {
      const sourceSrc = source.getAttribute('data-src');

      if (sourceSrc) {
        source.src = sourceSrc;
      }
    });
  }

  video.load();
  video.dataset.videoLoaded = 'true';
};

const resetVideo = (video) => {
  if (!video) return;

  video.pause();
  video.removeAttribute('src');
  video.currentTime = 0;
  video.dataset.videoLoaded = 'false';

  const sources = video.querySelectorAll('source');

  sources.forEach((source) => {
    source.removeAttribute('src');
  });

  video.load();
};

const getCurrentDevice = () => {
  return window.matchMedia(DESKTOP_BREAKPOINT).matches ? 'desktop' : 'mobile';
};

const getContainerType = (container) => {
  return container.getAttribute('data-video-lazy');
};

const getVideosByDevice = (container, device) => {
  return Array.from(
    container.querySelectorAll(`video[data-video-device="${device}"]`),
  );
};

const hideVideo = (video) => {
  video.classList.add('hidden');
  video.setAttribute('aria-hidden', 'true');
};

const showVideo = (video) => {
  video.classList.remove('hidden');
  video.removeAttribute('aria-hidden');
};

const initResponsiveBackground = async (container, device) => {
  const activeVideos = getVideosByDevice(container, device);
  const inactiveDevice = device === 'desktop' ? 'mobile' : 'desktop';
  const inactiveVideos = getVideosByDevice(container, inactiveDevice);

  inactiveVideos.forEach((video) => {
    resetVideo(video);
  });

  for (const video of activeVideos) {
    activateVideoSources(video);
    await safePlay(video);
  }
};

const initResponsiveSequence = async (container, device) => {
  const mobileVideos = getVideosByDevice(container, 'mobile');
  const desktopVideos = getVideosByDevice(container, 'desktop');

  if (device === 'mobile') {
    desktopVideos.forEach((video) => {
      resetVideo(video);
      hideVideo(video);
    });

    for (const video of mobileVideos) {
      showVideo(video);
      activateVideoSources(video);
      await safePlay(video);
    }

    return;
  }

  mobileVideos.forEach((video) => {
    resetVideo(video);
    hideVideo(video);
  });

  const part1 = container.querySelector(
    'video[data-video-device="desktop"][data-video-part="1"]',
  );
  const part2 = container.querySelector(
    'video[data-video-device="desktop"][data-video-part="2"]',
  );

  if (!part1) return;

  showVideo(part1);
  activateVideoSources(part1);

  if (part2) {
    hideVideo(part2);
    activateVideoSources(part2);

    if (part1.dataset.sequenceBound !== 'true') {
      let part2Started = false;

      part1.addEventListener('timeupdate', async () => {
        if (part2Started) return;

        if (!Number.isFinite(part1.duration) || part1.duration <= 0) return;

        const remainingTime = part1.duration - part1.currentTime;

        if (remainingTime <= 9) {
          part2Started = true;
          showVideo(part2);
          await safePlay(part2);
        }
      });

      part1.addEventListener('ended', () => {
        hideVideo(part1);
        showVideo(part2);
      });

      part1.dataset.sequenceBound = 'true';
    }
  }

  await safePlay(part1);
};

const initStandaloneVideo = async (container) => {
  const videos = Array.from(container.querySelectorAll('video'));

  for (const video of videos) {
    activateVideoSources(video);
    await safePlay(video);
  }
};

const initContainer = async (container) => {
  if (!container) return;

  const type = getContainerType(container);
  const device = getCurrentDevice();

  if (type === 'responsive-background') {
    await initResponsiveBackground(container, device);
    return;
  }

  if (type === 'responsive-sequence') {
    await initResponsiveSequence(container, device);
    return;
  }

  if (type === 'standalone') {
    await initStandaloneVideo(container);
  }
};

const videoLazyInit = () => {
  const containers = document.querySelectorAll('[data-video-lazy]');

  if (!containers.length) return;

  const observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  };

  const initializedContainers = new WeakSet();
  const mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT);

  const videoObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(async (entry) => {
      if (!entry.isIntersecting) return;

      const container = entry.target;

      await initContainer(container);
      initializedContainers.add(container);
      observer.unobserve(container);
    });
  }, observerOptions);

  containers.forEach((container) => {
    videoObserver.observe(container);
  });

  mediaQuery.addEventListener('change', async () => {
    for (const container of containers) {
      if (!initializedContainers.has(container)) continue;

      await initContainer(container);
    }
  });
};

export default videoLazyInit;